import EmptyMedicalCard from "../pages/patient-logs/components/empty-medical-cards";
import { Allergy, HealthCondition } from "../pages/payments/components/payment-details-sheet";
import healthConditionsImage from "~/assets/images/dashboard/health-condition.svg";
import allergiesImage from "~/assets/images/dashboard/allergies-medicals.svg";

type HealthSectionType = {
    healthConditions: HealthCondition[];
    title?: string
}

type AllergiesSectionType = {
    allergies: Allergy[];
    title?: string
}

type MedicalInfoType = {
    healthConditions: HealthCondition[];
    allergies: Allergy[];
    className: string;
    title: string
}

const HealthConditionsSection = ({ healthConditions, title = "Health Conditions" } : HealthSectionType) => {
  if (!healthConditions || healthConditions.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <h3 className="text-base font-medium mb-4 text-gray-700">{title}</h3>
      <div className="space-y-4">
        {healthConditions.map((condition, index) => (
          <HealthConditionCard key={index} condition={condition} />
        ))}
      </div>
    </div>
  );
};

const HealthConditionCard = ({ condition } : {condition : HealthCondition}) => {
  const fields: Array<{ key: keyof HealthCondition; label: string }> = [
    { key: 'status', label: 'Status' },
    { key: 'medication', label: 'Medication' },
    { key: 'hba1c', label: 'HbA1c' },
    { key: 'diagnosed', label: 'Diagnosed' }
  ];

  return (
    <div className="bg-rose-50 border-l-[4px] border-l-red-500 p-4">
      <h4 className="font-semibold text-gray-800 mb-3">{condition.name}</h4>
      <div className="space-y-1 text-sm text-gray-600">
        {fields.map(({ key, label }) => 
          condition[key] && (
            <div key={key}>
              <span className="font-medium">{label}:</span> {condition[key]}
            </div>
          )
        )}
      </div>
    </div>
  );
};

const AllergiesSection = ({ allergies, title = "Allergies" } : AllergiesSectionType) => {
  if (!allergies || allergies.length === 0) {
    return null;
  }

  return (
    <div>
      <h3 className="text-base font-medium mb-4 text-gray-700">{title}</h3>
      <div className="space-y-4">
        {allergies.map((allergy, index) => (
          <AllergyCard key={index} allergy={allergy} />
        ))}
      </div>
    </div>
  );
};

const AllergyCard = ({ allergy } : {allergy : Allergy}) => {
  const getSeverityColor = (severity : "Severe" | "Moderate" | "Mild") => {
    const colors = {
      'mild': 'bg-green-400',
      'moderate': 'bg-yellow-400',
      'severe': 'bg-red-400',
      'critical': 'bg-red-600'
    } as const;
    return colors[(severity?.toLowerCase() as keyof typeof colors) ?? ''] || 'bg-gray-400';
  };

  return (
    <div className="bg-[#F7F4FC] border-l-[4px] border-l-purple-500 p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-3 h-3 rounded-full ${getSeverityColor(allergy.severity)}`}></div>
        <h4 className="font-semibold text-gray-800">{allergy.name}</h4>
      </div>
      <div className="space-y-1 text-sm text-gray-600">
        <div>
          <span className="font-medium">Severity:</span> {allergy.severity}
        </div>
        {allergy.causes && allergy.causes.length > 0 && (
          <div className="flex items-start gap-1">
            <span>•</span>
            <span>
              <span className="font-medium">Causes:</span> {allergy.causes.join(", ")}.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const MedicalInfoSection = ({ healthConditions, allergies, className = "", title } : MedicalInfoType) => {
  return (
    <div className={className}>
    <h2 className="text-lg font-semibold mb-6 text-gray-800">{title}</h2>
      {healthConditions.length > 0 ? <HealthConditionsSection healthConditions={healthConditions} /> : <EmptyMedicalCard
        title="Health Conditions"
        image={healthConditionsImage}
        description=""
        altImg="Health Conditions"
        message="This patient has no recorded health conditions"
      />}
      {allergies.length > 0 ? <AllergiesSection allergies={allergies} /> : <EmptyMedicalCard
        title="Allergies"
        image={allergiesImage}
        description=""
        altImg="Allergies"
        message="This patient has no recorded allergies"
      />}
    </div>
  );
};


export { 
  HealthConditionsSection, 
  AllergiesSection, 
  MedicalInfoSection, 
  HealthConditionCard,
  AllergyCard
};